import { Helmet } from "react-helmet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Home, Users, Clock3, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import heroImage from "@/assets/blog/ai-real-estate.jpg";
import BlogArticleLayout from "@/components/BlogArticleLayout";

const AIForRealEstateAgents = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('blog.aiForRealEstate.metaTitle')}</title>
        <meta name="description" content={t('blog.aiForRealEstate.metaDescription')} />
        <meta name="keywords" content="AI for real estate agents, real estate AI phone agent, automated lead qualification real estate, AI property inquiry, real estate virtual assistant" />
        <link rel="canonical" href="https://call24x7.ai/blog/ai-for-real-estate-agents" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7 Team" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://call24x7.ai/blog/ai-for-real-estate-agents" />
        <meta property="og:title" content={t('blog.aiForRealEstate.title')} />
        <meta property="og:description" content={t('blog.aiForRealEstate.metaDescription')} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": t('blog.aiForRealEstate.title'),
            "description": t('blog.aiForRealEstate.metaDescription'),
            "author": { "@type": "Organization", "name": "Call24x7.AI" },
            "publisher": { "@type": "Organization", "name": "Call24x7.AI" },
            "datePublished": "2025-10-02",
            "dateModified": "2025-10-02"
          })}
        </script>
      </Helmet>

      <BlogArticleLayout
        title={t('blog.aiForRealEstate.title')}
        category={t('blog.categories.realEstate')}
        date="October 2, 2025"
        readTime={`7 ${t('blog.minRead')}`}
        heroImage={heroImage}
        heroAlt="AI phone agents for real estate"
      >
        <p className="text-xl text-muted-foreground mb-6">
          {t('blog.aiForRealEstate.intro')}
        </p>

        <div className="grid md:grid-cols-4 gap-4 my-8">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Home className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">40%</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiForRealEstate.stat1')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Users className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">100%</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiForRealEstate.stat2')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Clock3 className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">&lt;30s</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiForRealEstate.stat3')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <TrendingUp className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">24/7</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiForRealEstate.stat4')}</p>
            </CardContent>
          </Card>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiForRealEstate.problemTitle')}</h2>
        <p>{t('blog.aiForRealEstate.problemDesc1')}</p>
        <p className="mt-4">{t('blog.aiForRealEstate.problemDesc2')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiForRealEstate.howTransformsTitle')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">1. {t('blog.aiForRealEstate.feature1Title')}</h3>
        <p>{t('blog.aiForRealEstate.feature1Desc')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">2. {t('blog.aiForRealEstate.feature2Title')}</h3>
        <p>{t('blog.aiForRealEstate.feature2Desc')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">3. {t('blog.aiForRealEstate.feature3Title')}</h3>
        <p>{t('blog.aiForRealEstate.feature3Desc')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">4. {t('blog.aiForRealEstate.feature4Title')}</h3>
        <p>{t('blog.aiForRealEstate.feature4Desc')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiForRealEstate.resultsTitle')}</h2>
        <Card className="bg-muted/50 my-6">
          <CardContent className="pt-6">
            <p className="italic">{t('blog.aiForRealEstate.testimonial')}</p>
            <p className="mt-4 font-semibold">{t('blog.aiForRealEstate.testimonialAuthor')}</p>
          </CardContent>
        </Card>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiForRealEstate.gettingStartedTitle')}</h2>
        <p>{t('blog.aiForRealEstate.gettingStartedIntro')}</p>
        <ol className="list-decimal pl-6 my-4 space-y-2">
          <li>{t('blog.aiForRealEstate.step1')}</li>
          <li>{t('blog.aiForRealEstate.step2')}</li>
          <li>{t('blog.aiForRealEstate.step3')}</li>
          <li>{t('blog.aiForRealEstate.step4')}</li>
        </ol>

        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 mt-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.aiForRealEstate.ctaTitle')}</h3>
            <p className="text-muted-foreground mb-4">{t('blog.aiForRealEstate.ctaDesc')}</p>
            <Button onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
              {t('blog.aiForRealEstate.ctaButton')}
            </Button>
          </CardContent>
        </Card>
      </BlogArticleLayout>
    </>
  );
};

export default AIForRealEstateAgents;
